import type { ReactNode } from 'react'
import { scaleBand, scaleLinear } from '@visx/scale'
import { Group } from '@visx/group'
import { AxisBottom, AxisLeft } from '@visx/axis'
import type { ChartResult, ChartSpec } from '../../../types/chart.js'
import { CHART_AXIS_COLOR, CHART_GRID_COLOR, getSeriesStyle } from './chartTheme.js'
import type { ChartOverlayContext } from './chartOverlayTypes.js'

export interface BarChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
  compact?: boolean
  onSeriesClick?: (seriesKey: string) => void
  renderOverlay?: (ctx: ChartOverlayContext) => ReactNode
}

export function BarChart({
  spec,
  data,
  width = 500,
  height = 240,
  className = '',
  testId,
  compact = false,
  onSeriesClick,
  renderOverlay,
}: BarChartProps) {
  const isHorizontal = spec.style?.orientation === 'horizontal'
  const margin = compact
    ? { top: 2, right: 0, bottom: 2, left: 0 }
    : { top: 20, right: 20, bottom: 40, left: 50 }
  const xMax = Math.max(0, width - margin.left - margin.right)
  const yMax = Math.max(0, height - margin.top - margin.bottom)

  // Collect all distinct X keys
  const xKeys = Array.from(
    new Set(data.series.flatMap((s) => s.points.map((p) => String(p.x)))),
  )

  const allY = data.series.flatMap((s) => s.points.map((p) => p.y))
  const maxY = Math.max(...allY, 1)

  const xScale = scaleBand<string>({
    domain: xKeys,
    range: [0, xMax],
    padding: compact ? 0.15 : 0.3,
  })

  const yScale = scaleLinear<number>({
    domain: [0, maxY],
    range: [yMax, 0],
    nice: true,
  })

  const seriesCount = Math.max(data.series.length, 1)
  const subBarWidth = (xScale.bandwidth() || (compact ? 4 : 20)) / seriesCount

  return (
    <svg
      width={width}
      height={height}
      className={`chartSvg ${className}`}
      data-testid={testId}
      role="img"
      aria-label={spec.title || 'Bar Chart'}
    >
      <Group left={margin.left} top={margin.top}>
        {data.series.map((series, sIdx) => {
          const style = getSeriesStyle(sIdx, spec.style?.seriesColor?.[series.key])
          const seriesKey = series.key || String(sIdx)
          const isInteractive = Boolean(onSeriesClick)

          return (
            <Group
              key={seriesKey}
              tabIndex={isInteractive ? 0 : undefined}
              role={isInteractive ? 'button' : undefined}
              aria-label={isInteractive ? (series.label || seriesKey) : undefined}
              style={{ cursor: isInteractive ? 'pointer' : undefined }}
              onClick={isInteractive ? () => onSeriesClick?.(seriesKey) : undefined}
              onKeyDown={isInteractive ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSeriesClick?.(seriesKey)
                }
              } : undefined}
            >
              {series.points.map((point, pIdx) => {
                const xVal = String(point.x)
                const barX = (xScale(xVal) || 0) + sIdx * subBarWidth
                const barY = yScale(point.y)
                const barH = yMax - barY

                if (isHorizontal) {
                  // Horizontal layout
                  const hBarW = (point.y / maxY) * xMax
                  const hBarY = (xScale(xVal) || 0) + sIdx * subBarWidth
                  return (
                    <rect
                      key={`${series.key}-${pIdx}`}
                      x={0}
                      y={hBarY}
                      width={Math.max(0, hBarW)}
                      height={Math.max(compact ? 1 : 2, subBarWidth - (compact ? 1 : 2))}
                      fill={style.color}
                      strokeDasharray={style.strokeDasharray}
                      rx={compact ? 1 : 2}
                    />
                  )
                }

                return (
                  <rect
                    key={`${series.key}-${pIdx}`}
                    x={barX}
                    y={barY}
                    width={Math.max(1, subBarWidth - (compact ? 1 : 2))}
                    height={Math.max(0, barH)}
                    fill={style.color}
                    strokeDasharray={style.strokeDasharray}
                    rx={compact ? 1 : 2}
                  />
                )
              })}
            </Group>
          )
        })}

        {renderOverlay?.({
          xScale,
          yScale,
          margin,
          innerWidth: xMax,
          innerHeight: yMax,
        })}

        {!compact && (
          <>
            <AxisBottom
              top={yMax}
              scale={xScale}
              stroke={CHART_GRID_COLOR}
              tickStroke={CHART_GRID_COLOR}
              tickLabelProps={{
                fill: CHART_AXIS_COLOR,
                fontSize: 10,
                textAnchor: 'middle',
              }}
            />

            <AxisLeft
              scale={yScale}
              stroke={CHART_GRID_COLOR}
              tickStroke={CHART_GRID_COLOR}
              tickLabelProps={{
                fill: CHART_AXIS_COLOR,
                fontSize: 10,
                textAnchor: 'end',
                dx: -4,
              }}
            />
          </>
        )}
      </Group>
    </svg>
  )
}
